import React, { FC } from 'react';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

type MilestoneWarningType = 'warning' | 'error' | 'info';

type MilestoneWarningProps = {
  warningMessage: string;
  type?: MilestoneWarningType;
  sx?: SxProps<Theme>;
  iconSize?: string;
  fontSize?: string;
};

const WARNING_STYLES: Record<
  MilestoneWarningType,
  {
    backgroundColor: string;
    border: string;
    Icon: typeof WarningAmberRoundedIcon;
    iconColor: 'warning' | 'error' | 'info';
  }
> = {
  warning: {
    backgroundColor: '#F9F1E4',
    border: '1px solid #E5DED1',
    Icon: WarningAmberRoundedIcon,
    iconColor: 'warning',
  },
  error: {
    backgroundColor: '#FFF6F6',
    border: '1px solid #F5C6C6',
    Icon: WarningAmberRoundedIcon,
    iconColor: 'error',
  },
  info: {
    backgroundColor: '#E8F4FC',
    border: '1px solid #B8D4E8',
    Icon: InfoOutlinedIcon,
    iconColor: 'info',
  },
};

const MilestoneWarning: FC<MilestoneWarningProps> = ({
  warningMessage,
  type = 'warning',
  sx,
  iconSize = '26px',
  fontSize,
}) => {
  const { backgroundColor, border, Icon, iconColor } = WARNING_STYLES[type];
  const containerSx = {
    backgroundColor,
    border,
    borderRadius: '8px',
    p: '10px 12px',
    alignItems: 'center',
    gap: 1,
    mb: 2.75,
  };
  const mergedSx: SxProps<Theme> = sx
    ? [containerSx, ...(Array.isArray(sx) ? sx : [sx])]
    : containerSx;

  return (
    <Box display="flex" sx={mergedSx}>
      <Icon color={iconColor} sx={{ fontSize: iconSize, mr: 1 }} />
      <Typography variant="body1" sx={{ fontSize }}>
        {warningMessage}
      </Typography>
    </Box>
  );
};

export default MilestoneWarning;
